import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth.store';
import { LogOut, User, Settings } from 'lucide-react';

type Props = {
  profilePath?: string;
  placement?: 'top' | 'bottom';
  align?: 'left' | 'right';
  /** Bouton déconnexion toujours visible (sidebar admin) */
  showInlineLogout?: boolean;
  accentClass?: string;
};

export default function UserMenu({
  profilePath,
  placement = 'bottom',
  align = 'right',
  showInlineLogout = false,
  accentClass = 'bg-primary-700',
}: Props) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  if (!user) {
    return (
      <Link
        to="/login"
        className="block text-center text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 py-2.5 rounded-xl transition-colors"
      >
        {t('nav.login', { defaultValue: 'Connexion' })}
      </Link>
    );
  }

  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const menuPos =
    placement === 'top'
      ? 'bottom-full mb-2'
      : 'top-full mt-2';
  const alignCls = align === 'left' ? 'left-0' : 'right-0';

  function handleLogout() {
    setOpen(false);
    logout();
    navigate('/login');
  }

  return (
    <div className="relative space-y-2" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className={`w-9 h-9 ${accentClass} rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0`}>
          {initials}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-gray-900 truncate">
            {user.firstName} {user.lastName}
          </span>
          <span className="block text-xs text-gray-500 truncate">{user.email}</span>
        </span>
      </button>
      {open && (
        <div
          className={`absolute ${menuPos} ${alignCls} z-50 min-w-[200px] bg-white border border-gray-200 rounded-2xl shadow-xl py-2`}
        >
          {profilePath && (
            <Link
              to={profilePath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Settings size={16} />
              {t('nav.settings', { defaultValue: 'Paramètres' })}
            </Link>
          )}
          {user.role === 'customer' && (
            <Link
              to="/client/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <User size={16} />
              {t('client.profile.title', { defaultValue: 'Profil' })}
            </Link>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut size={16} />
            {t('nav.logout', { defaultValue: 'Déconnexion' })}
          </button>
        </div>
      )}
      {showInlineLogout && (
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors min-h-[44px]"
        >
          <LogOut size={16} aria-hidden />
          {t('nav.logout', { defaultValue: 'Déconnexion' })}
        </button>
      )}
    </div>
  );
}
